// 060 (gap-list H9): turn the `/pricing-templates` and `/currencies` payloads into the options the
// car form's rate-card Select offers. Pure, and outside the render, because 057 and 058 both shipped
// defects that had lived inside one — the same reason 050's `pricing-summary` and 058's `isUsable`
// are their own modules.
//
// ⚠️ The two payloads disagree about the id key: a template carries `_id`, a currency carries `id`.
// Joining on the wrong one yields a label with the name and no amounts — plausible output, a green
// suite, and no symbol. That is the shape of the 044 Gate B seam bug.
//
// The symbol ALWAYS comes from the template's own currency. `formatMinorUnits` in
// `PricingTemplateListPage` is page-private precisely because it hardcodes `RM`, which 050 FR-010
// forbids reusing for currency-aware output.

export interface RateCardTemplate {
  _id: string;
  supplier_id: string;
  name: string;
  status?: string;
  currency_id?: string;
  base_price_per_day_minor?: number;
  base_price_per_hour_minor?: number;
}

export interface Currency {
  id: string;
  symbol: string;
}

export interface RateCardOption {
  id: string;
  label: string;
}

function amount(minor: number | undefined, symbol: string, unit: string): string | null {
  if (typeof minor !== 'number' || !Number.isFinite(minor)) return null;
  const major = minor / 100;
  return `${symbol} ${Number.isInteger(major) ? major : major.toFixed(2)}/${unit}`;
}

/**
 * The assignable rate cards for one supplier, labelled for a person (FR-004, FR-006, FR-007).
 *
 * An unknown supplier yields NOTHING rather than everything (FR-005): the form learns the supplier
 * from the payload, and offering every supplier's rate cards while that resolves would invite the
 * cross-supplier assignment the platform now refuses. That falls out of the filter below — no
 * template's `supplier_id` equals `''` — so there is no separate early return to get wrong. An
 * explicit `if (!supplierId) return []` was written first and removed: it could not be made to fail,
 * because the filter already produced the same answer.
 *
 * A template whose currency is absent from the list keeps its name and loses its amounts — never an
 * assumed symbol.
 */
export function buildRateCardOptions(
  templates: RateCardTemplate[],
  currencies: Currency[],
  supplierId: string,
): RateCardOption[] {
  return templates
    .filter((t) => String(t.supplier_id) === String(supplierId))
    .map((t) => {
      const symbol = currencies.find((c) => c.id === t.currency_id)?.symbol;
      const parts = symbol
        ? [
            amount(t.base_price_per_day_minor, symbol, 'day'),
            amount(t.base_price_per_hour_minor, symbol, 'hour'),
          ].filter(Boolean)
        : [];
      // A non-active rate card is shown and marked, not hidden (FR-007): an operator cannot schedule
      // what they cannot see, and assignability deliberately ignores validity (FR-011). Whether a
      // rate resolves NOW is decided by H7's Publish rule.
      const suffix = t.status && t.status !== 'active' ? ` (${t.status})` : '';
      return { id: t._id, label: [t.name, ...parts].join(' · ') + suffix };
    });
}

export function storedRateCardIds(ids: string[] | undefined): string[] {
  return (ids ?? []).map(String);
}

/**
 * FR-016: a car storing more than one rate card must NOT be edited through a single Select — the
 * save would `$set` a one-element array over a priority list (index 0 wins, falling through) and
 * silently destroy the rest. Same replacement trap the car's location group carries.
 */
export function isMultiRateCard(ids: string[] | undefined): boolean {
  return storedRateCardIds(ids).length > 1;
}
